/**
 * PosterStyleSelector - 海报风格选择器组件
 * 使用 Collapse 组件实现展开/收起
 * 数据来源：usePosterTemplateData() 接口获取
 * 图片展示使用 BasicImage 组件
 *
 * 设计稿要求:
 * 1. 默认收起，header 显示 Frame 图标 + "风格: xxx" + 箭头
 * 2. 模板卡片：98×98，BasicImage(按语言取 coverUrl_cn / coverUrl_en)，选中态右上角打勾
 * 3. 自定义卡片：magic-poster-custom.svg 图标 + 渐变背景 + 蓝色边框
 * 4. 选中自定义后显示 contentEditable 输入区，点击即可编辑
 * 5. 顶部有边框线，图片列表支持横向滚动
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { useIntl } from 'umi';
import BasicImage from '@home/components/BasicImage';
import { Collapse } from '@/pages/aigc/components/Collapse';
import { usePosterTemplateData } from '../../../../../Home/hooks/usePosterTemplateData';
import posterStyleIcon from '@/pages/aigc/assets/svg/poster-style-icon.svg';
import posterStyleArrow from '@/pages/aigc/assets/svg/poster-style-arrow.svg';
import magicPosterCustomIcon from '@/pages/aigc/assets/svg/magic-poster-custom.svg';
import magicPosterCustomDefaultIcon from '@/pages/aigc/assets/svg/magic-poster-custom-default.svg';
import magicPosterCustomRadio from '@/pages/aigc/assets/svg/magic-poster-custom-radio.svg';
import { IPosterStyleSelectorProps } from '../../types';
import useSlideEffect from '@intelligence/pages/TrendingGames/components/GameSelect/_hooks/useSlideEffect';
import SwiperBtn from '@/pages/aigc/pages/Agent/components/ImgUploadRenderList/component/SwiperBtn';
import './PosterStyleSelector.scss';
import { LANGUAGE_EN } from '@home/_config/config';

/** 自定义模板的固定 ID */
const CUSTOM_TEMPLATE_ID = 'custom';

export const PosterStyleSelector: React.FC<IPosterStyleSelectorProps> = ({
  selectedStyle,
  onStyleChange,
  disabled = false,
  className,
}) => {
  const { formatMessage, locale } = useIntl();
  const isEN = locale === LANGUAGE_EN;

  // 从接口获取海报灵感模板数据
  const { data: posterTemplates } = usePosterTemplateData();

  // 展开/收起状态（默认收起）
  const [isOpen, setIsOpen] = useState(false);

  // contentEditable div 的 ref
  const editableRef = useRef<HTMLDivElement>(null);

  // 画廊左右滚动按钮逻辑（参照 Copilot 文件上传列表）
  const {
    containerRef,
    listContainerRef,
    isOverflow,
    slideDisabledState,
    handleSlideLeft,
    handleSlideRight,
  } = useSlideEffect({
    othersWidth: 80, // 左右按钮 + 间距占用的宽度
    updateDeps: [posterTemplates, isOpen],
  });

  const isCustomSelected = selectedStyle?.template?.isCustom === true;

  // 当前选中的模板 ID
  const selectedTemplateId = selectedStyle?.template?.id;

  // 头部显示的选中风格文案（动态查询模板数据）
  const selectedLabel = useMemo(() => {
    if (isCustomSelected) {
      return formatMessage({ id: 'dba_poster_style_custom' });
    }
    // 模板数据尚未加载完成时，显示加载中而非"未选择"
    if (!posterTemplates?.[0]?.list) {
      return '';
    }
    if (selectedTemplateId) {
      // 通过 templateId 查询模板名称
      const template = posterTemplates[0].list.find((tpl: any) =>
        (tpl.templateId || tpl.id) === selectedTemplateId
      );
      if (template?.titleCn) return template.titleCn;
    }
    // 兜底：使用第一个模板的名称，避免显示"未选择"
    const firstTemplate = posterTemplates[0].list[0];
    return firstTemplate?.titleCn || formatMessage({ id: 'dba_poster_style_not_selected' });
  }, [isCustomSelected, selectedTemplateId, posterTemplates]);

  // 点击 header 切换展开/收起
  const handleToggle = useCallback(() => {
    if (disabled) return;
    setIsOpen((prev) => !prev);
  }, [disabled]);

  // 点击模板卡片
  const handleTemplateClick = useCallback(
    (templateData: any) => {
      if (disabled) return;

      if (templateData.id === CUSTOM_TEMPLATE_ID) {
        // 选中自定义：只标记为 isCustom
        onStyleChange({
          template: {
            id: CUSTOM_TEMPLATE_ID,
            isCustom: true,
          },
          customInput: selectedStyle?.customInput || '',
        });
        // 延迟一帧聚焦，确保 DOM 已渲染，并将光标移到末尾
        requestAnimationFrame(() => {
          const el = editableRef.current;
          if (el) {
            el.focus();
            // 将光标移到文本末尾
            const range = document.createRange();
            const sel = window.getSelection();
            if (el.childNodes.length > 0) {
              range.selectNodeContents(el);
              range.collapse(false); // collapse to end
            } else {
              range.setStart(el, 0);
              range.collapse(true);
            }
            sel?.removeAllRanges();
            sel?.addRange(range);
          }
        });
      } else {
        // 选中预设模板：只传递 templateId
        onStyleChange({
          template: {
            id: templateData.templateId || templateData.id,
          },
          customInput: '',
        });
      }
    },
    [disabled, onStyleChange, selectedStyle?.customInput],
  );

  // contentEditable 输入同步
  const handleEditableInput = useCallback(() => {
    const text = editableRef.current?.textContent || '';
    onStyleChange({
      template: selectedStyle?.template || null,
      customInput: text,
    });
  }, [onStyleChange, selectedStyle?.template]);

  // 仅在非聚焦状态下同步外部 prop 到 contentEditable DOM，避免编辑时光标重置
  useEffect(() => {
    const el = editableRef.current;
    if (!el) return;
    // 如果当前正在聚焦（用户正在编辑），不同步，避免光标重置
    if (document.activeElement === el) return;
    const newText = selectedStyle?.customInput || '';
    if (el.textContent !== newText) {
      el.textContent = newText;
    }
  }, [selectedStyle?.customInput]);

  return (
    <div
      className={classNames(
        'dba-poster-style-selector',
        { 'dba-poster-style-selector--disabled': disabled },
        className,
      )}
    >
      {/* 风格标签头：默认收起，点击展开/收起画廊 */}
      <div
        className={classNames('dba-poster-style-selector__header', {
          'dba-poster-style-selector__header--open': isOpen,
        })}
        onClick={handleToggle}
      >
        <img
          src={posterStyleIcon}
          alt=""
          className="dba-poster-style-selector__title-icon"
        />
        <span className="dba-poster-style-selector__label">
          {formatMessage({ id: 'dba_poster_style_label' })}
        </span>
        <span className="dba-poster-style-selector__value">
          {selectedLabel}
        </span>
        <img
          src={posterStyleArrow}
          alt=""
          className={classNames('dba-poster-style-selector__arrow', {
            'dba-poster-style-selector__arrow--open': isOpen,
          })}
        />
      </div>

      {/* 使用 Collapse 组件实现展开/收起 */}
      <Collapse isOpen={isOpen} transition="height 250ms cubic-bezier(0.4, 0, 0.2, 1)">
        <div className="dba-poster-style-selector__body">
          {/* 风格模板卡片画廊（带左右滚动按钮） */}
          <div ref={containerRef} className="dba-poster-style-selector__gallery-swiper">
            {isOverflow && (
              <SwiperBtn
                gradient
                type="prev"
                disabled={slideDisabledState.prev}
                onClick={handleSlideLeft}
                iconType="arrowleft"
              />
            )}
            <div ref={listContainerRef} className="dba-poster-style-selector__gallery">
              {/* 接口模板列表 */}
              {posterTemplates?.[0]?.list?.map((tpl: any) => {
                const tplId = tpl.templateId || tpl.id;
                const isActive = selectedTemplateId === tplId;
                return (
                  <div
                    key={tplId}
                    className={classNames('dba-poster-style-selector__card', {
                      'dba-poster-style-selector__card--active': isActive,
                    })}
                    onClick={() =>
                      handleTemplateClick({
                        ...tpl,
                        id: tplId,
                      })
                    }
                  >
                    {/* 选中态右上角打勾标记 */}
                    {isActive && (
                      <div className="dba-poster-style-selector__card-check">
                        <img src={magicPosterCustomRadio} alt="" className="dba-poster-style-selector__card-check-icon" />
                      </div>
                    )}
                    <div className="dba-poster-style-selector__card-image">
                      {(tpl.coverUrl_cn || tpl.coverUrl_en)  ? (
                        <BasicImage
                          src={isEN ? tpl.coverUrl_en : tpl.coverUrl_cn}
                          alt={tpl.titleCn || tpl.titleEn}
                          className="dba-poster-style-selector__card-img"
                        />
                      ) : (
                        <div className="dba-poster-style-selector__card-placeholder" />
                      )}
                      <div className="dba-poster-style-selector__card-title" title={tpl.titleCn || tpl.titleEn}>
                        {tpl.titleCn || tpl.titleEn}
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* 自定义卡片 */}
              <div
                className={classNames(
                  'dba-poster-style-selector__card',
                  'dba-poster-style-selector__card--custom',
                  { 'dba-poster-style-selector__card--active': isCustomSelected },
                )}
                onClick={() => handleTemplateClick({ id: CUSTOM_TEMPLATE_ID })}
              >
                {/* 选中态右上角打勾标记 */}
                {isCustomSelected && (
                  <div className="dba-poster-style-selector__card-check">
                    <img src={magicPosterCustomRadio} alt="" className="dba-poster-style-selector__card-check-icon" />
                  </div>
                )}
                <div className="dba-poster-style-selector__card-custom-icon">
                  <img src={isCustomSelected ? magicPosterCustomIcon : magicPosterCustomDefaultIcon} alt="Custom" />
                </div>
                <span className="dba-poster-style-selector__card-label">
                  {formatMessage({ id: 'dba_poster_style_custom' })}
                </span>
              </div>
            </div>
            {isOverflow && (
              <SwiperBtn
                gradient
                type="next"
                disabled={slideDisabledState.next}
                onClick={handleSlideRight}
                iconType="arrowleft"
              />
            )}
          </div>

          {/* 自定义输入区域：contentEditable，点击即可编辑，无需状态切换 */}
          {isCustomSelected && (
            <div
              ref={editableRef}
              className="dba-poster-style-selector__editable"
              contentEditable={!disabled}
              suppressContentEditableWarning
              data-placeholder={formatMessage({ id: 'dba_poster_style_custom_placeholder' })}
              onInput={handleEditableInput}
            />
          )}
        </div>
      </Collapse>
    </div>
  );
};

export default PosterStyleSelector;
